import React from 'react';
import { useProvidedState } from '@shared-ui/provided-state-context';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m)
        return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done)
            ar.push(r.value);
    }
    catch (error) {
        e = { error: error };
    }
    finally {
        try {
            if (r && !r.done && (m = i["return"]))
                m.call(i);
        }
        finally {
            if (e)
                throw e.error;
        }
    }
    return ar;
}
function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2)
        for (var i = 0, l = from.length, ar; i < l; i++) {
            if (ar || !(i in from)) {
                if (!ar)
                    ar = Array.prototype.slice.call(from, 0, i);
                ar[i] = from[i];
            }
        }
    return to.concat(ar || Array.prototype.slice.call(from));
}

var SignalContext = React.createContext(undefined);
var SignalProvider = function (_a) {
    var children = _a.children;
    var _b = __read(React.useState([]), 2), subscribers = _b[0], setSubscribers = _b[1];
    var signalsRegistry = React.useMemo(function () { return []; }, []);
    var registerSignal = React.useCallback(function (signal) {
        var _a, _b;
        if (!signal) {
            return {
                subscribe: null
            };
        }
        /**
         * Only push the signal to the registry if it isn't already in the registry
         */
        if (!signalsRegistry.find(function (signalInRegistry) { return Object.is(signalInRegistry, signal); })) {
            signalsRegistry.push(signal);
        }
        if (signal.reference) {
            return {
                subscribe: (_a = subscribers.find(function (s) { return s.result.reference === signal.reference; })) !== null && _a !== void 0 ? _a : null
            };
        }
        return {
            subscribe: (_b = subscribers.find(function (s) { return s.result.type === signal.type; })) !== null && _b !== void 0 ? _b : null
        };
    }, [signalsRegistry, subscribers]);
    var onSignalEmit = React.useCallback(function (signal, callback) {
        var subscribe = registerSignal(signal).subscribe;
        if (subscribe && signal) {
            if (signal.reference && subscribe.result.reference && signal.reference === subscribe.result.reference) {
                // If there is a reference given in the signal and it matches the result; use callback
                callback(subscribe);
            }
            if (!signal.reference && signal.type === subscribe.result.type) {
                // If no reference is provided but signal type matches subscribe type; use callback.
                callback(subscribe);
            }
        }
    }, [registerSignal]);
    var emitSignals = React.useCallback(function (signals) {
        var typesInRegistry = signalsRegistry.map(function (signal) { return signal.type; });
        var result = signals
            .filter(function (emitSignal) { return typesInRegistry.includes(emitSignal.signal.type); })
            .map(function (emitSignalInRegistry) {
            var _a;
            return ({
                result: {
                    type: emitSignalInRegistry.signal.type,
                    reference: (_a = emitSignalInRegistry.signal.reference) !== null && _a !== void 0 ? _a : undefined,
                    values: emitSignalInRegistry.values
                }
            });
        });
        setSubscribers(result);
    }, [signalsRegistry]);
    var context = React.useMemo(function () { return ({
        onSignalEmit: onSignalEmit,
        emitSignals: emitSignals
    }); }, [onSignalEmit, emitSignals]);
    return React.createElement(SignalContext.Provider, { value: context }, children);
};

function useSignals() {
    var signalContext = React.useContext(SignalContext);
    if (signalContext === undefined) {
        throw new Error('No Trips SignalProvider found in React tree');
    }
    return signalContext;
}

/**
 * useEmitSignalsHandler will return an object with an emit function. This function will scan through
 * the emitting value types and will update/populate values according to it's type. The updated value
 * returned should be primitive.
 *
 * Note that this function has a dependency on the ValidatedInputsState chunk that needs to be included
 * in the StateProvider somewhere higher in the react tree.
 *
 * @param {TripsEmitSignalFragment[]} emitSignalValues Supplied values given by the experience API. This is raw response data.
 */
var useEmitSignalsHandler = function (emitSignalValues) {
    var emitSignals = useSignals().emitSignals;
    var _a = __read(useProvidedState(), 1), state = _a[0];
    return {
        emit: function () {
            var emitValues = emitSignalValues === null || emitSignalValues === void 0 ? void 0 : emitSignalValues.map(function (signal) {
                var values = signal.values.map(function (value) {
                    var _a;
                    switch (value.value.__typename) {
                        case 'TripsSignalFieldIdValue': {
                            var fieldIdValue_1 = value.value;
                            var inputsValue = state.validatedInputs.inputFields.find(function (inputField) { return inputField.inputId === fieldIdValue_1.id; });
                            return {
                                __typename: value.__typename,
                                key: value.key,
                                value: inputsValue.inputValue
                            };
                        }
                        case 'TripsSignalFieldIdExistingValues': {
                            var fieldIdExistingValues = value.value;
                            var inputValues = fieldIdExistingValues.ids
                                .map(function (id) { return state.validatedInputs.inputFields.find(function (inputField) { return inputField.inputId === id; }); })
                                .map(function (inputField) { return inputField.inputValue; });
                            var combinedValues = __spreadArray(__spreadArray([], __read(((_a = fieldIdExistingValues.prefixes) !== null && _a !== void 0 ? _a : [])), false), __read(inputValues), false);
                            return {
                                __typename: value.__typename,
                                key: value.key,
                                value: combinedValues
                            };
                        }
                        default:
                            return {
                                __typename: value.__typename,
                                key: value.key,
                                value: null
                            };
                    }
                });
                return {
                    __typename: signal.__typename,
                    signal: signal.signal,
                    values: values
                };
            });
            if (emitValues) {
                emitSignals(emitValues);
            }
        }
    };
};

export { SignalProvider, useEmitSignalsHandler, useSignals };
